<template>
  <v-dialog
    v-model="dialogVisible"
    max-width="760px"
    scrollable
    :fullscreen="$vuetify.breakpoint.xsOnly"
    transition="dialog-bottom-transition"
  >
    <v-card class="timeline-card">
      <!-- Header -->
      <div class="tl-head">
        <div class="tl-head-main">
          <div class="tl-head-icon">
            <v-icon color="white">mdi-timeline-clock-outline</v-icon>
          </div>
          <div>
            <div class="tl-head-title">Application timeline</div>
            <div class="tl-head-subtitle">
              <template v-if="loading">Loading history…</template>
              <template v-else>{{ records.length }} step{{ records.length === 1 ? "" : "s" }} recorded</template>
            </div>
          </div>
        </div>
        <v-btn icon dark aria-label="Close" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <!-- Application summary -->
      <div class="tl-summary">
        <div class="tl-summary-main">
          <span class="app-no">{{ appNo }}</span>
          <div class="tl-summary-text">
            <div v-if="filmTitle" class="tl-camp">
              <v-icon x-small color="#15803d">mdi-movie-open-outline</v-icon>
              {{ filmTitle }}
            </div>
            <div v-if="applicantName" class="tl-applicant">Applicant · {{ applicantName }}</div>
          </div>
        </div>
        <div v-if="currentHandler" class="tl-handler">
          <span class="tl-handler-dot" :class="{ 'tl-handler-dot--closed': isClosed }"></span>
          {{ currentHandler }}
        </div>
      </div>

      <!-- Body -->
      <v-card-text class="tl-body">
        <div v-if="loading" class="tl-state">
          <v-progress-circular indeterminate color="#15803d" size="44" width="4"></v-progress-circular>
          <div>Loading timeline records…</div>
        </div>

        <TimelineView v-else-if="records && records.length > 0" :records="records" :application="application" />

        <div v-else class="tl-state">
          <v-icon size="48" color="#cbd5e1">mdi-timeline-remove-outline</v-icon>
          <div>No timeline records found for this application.</div>
        </div>
      </v-card-text>

      <!-- Footer -->
      <div class="tl-footer">
        <v-btn v-if="appId" depressed class="btn-act btn-act--info" @click="goToRecordsPage">
          <v-icon small left>mdi-open-in-new</v-icon>
          Full records view
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn depressed class="btn-act btn-act--primary px-6" @click="close">
          Close
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from "axios";
import TimelineView, { journeyRecords } from "@/components/Common/TimelineView.vue";
import { currentlyWithLabel } from "@/components/Common/deskConfig";

// Timeline pop-up of a film shooting application (Nature Camp layout; the
// stops themselves are drawn by TimelineView).
export default {
  name: "TimelineDialog",
  components: { TimelineView },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    application: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      records: [],
    };
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    appId() {
      return (this.application && (this.application._id || this.application.id)) || null;
    },
    isClosed() {
      return !!this.application && !!this.application.status && this.application.status !== "Active";
    },
    appNo() {
      return (this.application && this.application.applicationNo) || "N/A";
    },
    filmTitle() {
      const a = this.application || {};
      const first = a.filmShootingDetails && a.filmShootingDetails[0];
      if (!first) return a.natureOfApplication || "";
      return [first.filmName, first.location].filter(Boolean).join(" · ");
    },
    applicantName() {
      return (this.application && this.application.applicantName) || "";
    },
    currentHandler() {
      return currentlyWithLabel(this.application);
    },
  },
  watch: {
    dialogVisible(val) {
      if (val && this.appId) {
        this.fetchTimeline();
      } else if (!val) {
        this.records = [];
      }
    },
    appId(newVal) {
      if (this.dialogVisible && newVal) {
        this.fetchTimeline();
      }
    },
  },
  methods: {
    close() {
      this.dialogVisible = false;
    },
    goToRecordsPage() {
      if (this.appId) {
        this.$router.push(`/records?id=${this.appId}`).catch(() => {});
        this.close();
      }
    },
    fetchTimeline() {
      if (!this.appId) return;
      this.loading = true;
      axios({
        method: "POST",
        url: "/records/application",
        headers: { token: localStorage.getItem("token") },
        data: { appid: this.appId },
      })
        .then((response) => {
          this.loading = false;
          const data = response.data && response.data.status && Array.isArray(response.data.data) ? response.data.data : [];
          this.records = journeyRecords(data);
        })
        .catch((err) => {
          this.loading = false;
          console.error("Error fetching application timeline:", err);
          this.records = [];
        });
    },
  },
};
</script>

<style scoped>
.timeline-card {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.tl-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 16px 18px 24px;
  background: linear-gradient(120deg, #052e16 0%, #14532d 55%, #15803d 100%);
  color: #ffffff;
}

.tl-head-main {
  display: flex;
  align-items: center;
  gap: 14px;
}

.tl-head-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.16);
}

.tl-head-title {
  font-size: 18px;
  font-weight: 800;
  line-height: 1.25;
}

.tl-head-subtitle {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
}

.tl-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px;
  background: #f3faf5;
  border-bottom: 1px solid #e3eee6;
}

.tl-summary-main {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}

.tl-summary-text {
  min-width: 0;
}

.tl-camp {
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
}

.tl-applicant {
  font-size: 12.5px;
  color: #64748b;
}

.tl-handler {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffffff;
  border: 1px solid #d7ebdd;
  font-size: 12.5px;
  font-weight: 700;
  color: #14532d;
}

.tl-handler-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.2);
}

.tl-handler-dot--closed {
  background: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.25);
}

.tl-body {
  max-height: 540px;
  padding: 24px !important;
  background: #fbfdfb;
}

.tl-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 16px;
  color: #64748b;
  font-weight: 600;
  text-align: center;
}

.tl-footer {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e3eee6;
  background: #ffffff;
}

@media (max-width: 600px) {
  .tl-head {
    padding: 14px 8px 14px 16px;
  }

  .tl-summary {
    padding: 12px 16px;
  }

  .tl-body {
    max-height: none;
    padding: 16px 12px !important;
  }

  .tl-footer {
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
  }
}
</style>
